import { useState, useRef, useCallback, useEffect } from 'react'
import { YStack, Text, XStack, Button, themeable, Spinner } from 'tamagui'
import { ZoomIn, ZoomOut, FileText } from '@tamagui/lucide-icons'

type Point = { x: number; y: number }

export const Certificate = ({ url }: { url: string }) => {
  const [isPdfLoading, setIsPdfLoading] = useState(true)
  const [scale, setScale] = useState(1.0)

  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const lastPos = useRef<Point>({ x: 0, y: 0 })
  const lastPinchDist = useRef<number | null>(null)
  const currentScale = useRef(1.0)
  const currentOffset = useRef<Point>({ x: 0, y: 0 })

  const cleanUrl = url ? `${url.replace('http://', 'https://')}#view=FitH&toolbar=0` : ''

  useEffect(() => {
    if (url) {
      setIsPdfLoading(true)
      setScale(1)
      currentScale.current = 1
      currentOffset.current = { x: 0, y: 0 }
    }
  }, [url])

  const applyTransform = useCallback((s: number, o: Point) => {
    const el = containerRef.current?.querySelector<HTMLDivElement>('.pdf-inner')
    if (el) {
      el.style.transform = `translate(${o.x}px, ${o.y}px) scale(${s})`
    }
  }, [])

  // ── Mouse events ──────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    if (currentScale.current <= 1) return
    isDragging.current = true
    lastPos.current = { x: e.clientX, y: e.clientY }
  }

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return
    const dx = e.clientX - lastPos.current.x
    const dy = e.clientY - lastPos.current.y
    lastPos.current = { x: e.clientX, y: e.clientY }
    currentOffset.current = {
      x: currentOffset.current.x + dx,
      y: currentOffset.current.y + dy,
    }
    applyTransform(currentScale.current, currentOffset.current)
  }

  const onMouseUp = () => {
    isDragging.current = false
  }

  // ── Wheel zoom ────────────────────────────────────────────────
  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.15 : 0.15
      const newScale = Math.max(1, Math.min(4, currentScale.current + delta))
      if (newScale === 1) currentOffset.current = { x: 0, y: 0 }
      currentScale.current = newScale
      applyTransform(newScale, currentOffset.current)
      setScale(newScale)
    },
    [applyTransform]
  )

  // ── Touch events via useEffect (passive: false) ───────────────
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX
        const dy = e.touches[0].clientY - e.touches[1].clientY
        lastPinchDist.current = Math.hypot(dx, dy)
      } else if (e.touches.length === 1 && currentScale.current > 1) {
        lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      if (e.touches.length === 2 && lastPinchDist.current !== null) {
        const dx = e.touches[0].clientX - e.touches[1].clientX
        const dy = e.touches[0].clientY - e.touches[1].clientY
        const dist = Math.hypot(dx, dy)
        const ratio = dist / lastPinchDist.current
        const newScale = Math.max(1, Math.min(4, currentScale.current * ratio))
        lastPinchDist.current = dist
        if (newScale === 1) currentOffset.current = { x: 0, y: 0 }
        currentScale.current = newScale
        applyTransform(newScale, currentOffset.current)
        setScale(newScale)
      } else if (e.touches.length === 1 && currentScale.current > 1) {
        const dx = e.touches[0].clientX - lastPos.current.x
        const dy = e.touches[0].clientY - lastPos.current.y
        lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        currentOffset.current = {
          x: currentOffset.current.x + dx,
          y: currentOffset.current.y + dy,
        }
        applyTransform(currentScale.current, currentOffset.current)
      }
    }

    const handleTouchEnd = () => {
      lastPinchDist.current = null
    }

    el.addEventListener('touchstart', handleTouchStart, { passive: false })
    el.addEventListener('touchmove', handleTouchMove, { passive: false })
    el.addEventListener('touchend', handleTouchEnd)

    return () => {
      el.removeEventListener('touchstart', handleTouchStart)
      el.removeEventListener('touchmove', handleTouchMove)
      el.removeEventListener('touchend', handleTouchEnd)
    }
  }, [applyTransform])

  // ── Toolbar zoom buttons ──────────────────────────────────────
  const handleZoom = (delta: number) => {
    const newScale = Math.max(1, Math.min(4, currentScale.current + delta))
    if (newScale === 1) currentOffset.current = { x: 0, y: 0 }
    currentScale.current = newScale
    applyTransform(newScale, currentOffset.current)
    setScale(newScale)
  }

  return (
    <YStack f={1} backgroundColor="white" borderRadius="$4" overflow="hidden">
      <XStack
        p="$2"
        $gtSm={{ p: '$3' }}
        ai="center"
        jc="space-between"
        borderBottomWidth={1}
        borderColor="$borderColor"
      >
        <XStack ai="center" gap="$2" f={1}>
          <FileText size={18} color="$blue10" />
          <Text fow="bold" fontSize="$2" $gtSm={{ fontSize: '$4' }} col="$color" numberOfLines={1}>
            Vista Previa del Documento
          </Text>
        </XStack>
        <XStack gap="$1" ai="center">
          <ToolbarButton IconComponent={ZoomOut} onPress={() => handleZoom(-0.4)} />
          <Text fontSize="$1" $gtSm={{ fontSize: '$3' }} fow="bold" minWidth={45} ta="center">
            {`${Math.round(scale * 100)}%`}
          </Text>
          <ToolbarButton IconComponent={ZoomIn} onPress={() => handleZoom(0.4)} />
        </XStack>
      </XStack>

      <YStack f={1} backgroundColor="#f0f2f5" p="$1" $gtSm={{ p: '$4' }}>
        <div
          ref={containerRef}
          style={{
            flex: 1,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: 8,
            backgroundColor: 'white',
            cursor: scale > 1 ? 'grab' : 'default',
            userSelect: 'none',
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onWheel={onWheel}
        >
          {url && isPdfLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backgroundColor: 'white',
              }}
            >
              <Spinner size="large" color="$blue10" />
            </div>
          )}

          {!url ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <span>Esperando documento...</span>
            </div>
          ) : (
            <div
              className="pdf-inner"
              style={{
                width: '100%',
                height: '100%',
                transformOrigin: '0 0',
                willChange: 'transform',
              }}
            >
              <iframe
                src={cleanUrl}
                width="100%"
                height="100%"
                style={{
                  border: 'none',
                  display: 'block',
                  pointerEvents: scale > 1 ? 'none' : 'auto',
                  minHeight: '850px',
                }}
                onLoad={() => setIsPdfLoading(false)}
              />
            </div>
          )}
        </div>
      </YStack>
    </YStack>
  )
}

const ToolbarButton = themeable(({ IconComponent, ...props }: any) => (
  <Button
    size="$3"
    circular
    backgroundColor="transparent"
    hoverStyle={{ backgroundColor: '$gray4' }}
    pressStyle={{ scale: 0.9 }}
    {...props}
  >
    <IconComponent size={20} color="$color" />
  </Button>
))
